import React, {
  forwardRef,
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
} from 'react';
import {
  ReactFlow,
  Background,
  MarkerType,
  addEdge,
  applyNodeChanges,
  applyEdgeChanges,
  Controls,
  ControlButton,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { toast } from 'react-toastify';
import { useSelector, useDispatch } from 'react-redux';
// eslint-disable-next-line
import {
  setNodesForWorkflow,
  setEdgesForWorkflow,
  setConnectionDetailsForWorkflow,
  removeEdgeAndUnmapFields,
  saveMappingThunk,
  clearFuzzyStateForWorkflow,
  setExpandAllTrigger,
  setBlinkByWorkflow,
  // setAutoFuzzyEnabled,
  setFilterExpressionForWorkflow,
} from '../../state/slices/mappingSlice';
import ProceedMappingDialog from '../ProceedMappingDialog';
import SourceNode from './SourceNode';
import DestinationNode from './DestinationNode';
import FunctionNode from './FunctionNode';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  useTheme,
} from '@mui/material';
import FunctionToolbar from './FunctionToolbar';
import PropTypes from 'prop-types';
import CustomEdge from './Edges/CustomEdge';
import EntitySelectionDialog from './EntitySelectionDialog';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useThemeContext } from '../../Context/ThemeContext';
import { cloneDeepWith } from 'lodash';
import ConfirmDialog from './ConfirmDialog';
import SaveCloseDialog from './SaveCloseDialog';
import EdgeInfoBox from './EdgeInfoBox';
import { getAllTypeAliases } from '../../utils/formatters/stringFormatter';
import { Add, CenterFocusStrong, Remove } from '@mui/icons-material';

const edgeTypes = {
  custom: CustomEdge,
};

const functionTypeCompatibility = {
  substring: ['string'],
  aggregation: ['string'],
  field_split: ['string'],
  trim: ['string'],
  remove_special_characters: ['string', 'character'],
  currency_format: ['number', 'int', 'integer', 'float', 'double'],
  format_date: ['string', 'date', 'datetime', 'timestamp'],
  add_date: ['string', 'date', 'datetime', 'timestamp'],
  order_by: ['any'],
  map: ['any'],
  concat: ['string'],
  uppercase: ['string'],
  lowercase: ['string'],
  length: ['string'],
  replace: ['string'],
  mean: ['number', 'integer', 'int', 'string'],
  median: ['number', 'integer', 'int', 'string'],
  mode: ['number', 'integer', 'int', 'string'],
  to_timestamp: ['date', 'timestamp', 'datetime', 'string'],
  extract: ['string', 'date', 'datetime', 'timestamp'],
  day_of_week: ['string', 'date', 'datetime', 'timestamp'],
  padLeftRight: ['string'],
  parse_int: ['string', 'double'],
  parse_double: ['integer', 'int', 'string'],
  convert_to_string: ['integer', 'number', 'int', 'string', 'bit'],
  join: ['string'],
  lookup: ['string'],
  relation: ['string'],
  registry: ['reference'],
  data_masking: ['string', 'number', 'int', 'integer', 'float', 'double', 'char', 'varchar', 'varchar2', 'text', 'numeric', 'decimal', 'smallint', 'bigint'],
};

const isNumericStringField = (fieldObj) => {
  if (!fieldObj || fieldObj.type !== 'string') return false;

  const numericPatterns = [
    /id$/i,
    /number$/i,
    /amount$/i,
    /price$/i,
    /cost$/i,
    /value$/i,
    /count$/i,
    /quantity$/i,
    /total$/i,
    /sum$/i,
    /code$/i,
    /zip$/i,
    /phone$/i,
    /mobile$/i,
    /ssn$/i,
    /tax.*id$/i,
  ];
  const fieldName = fieldObj.label?.toLowerCase() || '';
  const hasNumericPattern = numericPatterns.some((pattern) =>
    pattern.test(fieldName),
  );

  return hasNumericPattern;
};

const functionOutputTypes = {
  substring: ['string'],
  aggregation: ['any'],
  field_split: ['string'],
  trim: ['string'],
  remove_special_characters: ['string'],
  currency_format: ['string'],
  format_date: ['string', 'date', 'datetime'],
  add_date: ['string', 'date', 'datetime'],
  order_by: ['any'],
  map: ['any'],
  concat: ['string'],
  uppercase: ['string'],
  lowercase: ['string'],
  length: ['number'],
  replace: ['string'],
  mean: ['number'],
  median: ['number'],
  mode: ['number'],
  to_timestamp: ['date', 'datetime', 'timestamp'],
  extract: ['string', 'date', 'datetime', 'timestamp'],
  day_of_week: ['string', 'date', 'datetime', 'timestamp'],
  padLeftRight: ['string'],
  parse_int: ['integer'],
  parse_double: ['double'],
  convert_to_string: ['string'],
  join: ['any'],
  lookup: ['any'],
  relation: ['any'],
  registry: ['reference'],
  data_masking: ['string'],
};

const getActualHandle = (edge, which) => {
  if (which === 'source') {
    return edge.data?._originalSourceHandle || edge.sourceHandle;
  }
  if (which === 'target') {
    return edge.data?._prev || edge.targetHandle;
  }
  return null;
};

const getContainedButtonStyle = (theme) => ({
  fontSize: '0.775rem',
  backgroundColor: '#5A66A6',
  padding: '0.2rem 0.5rem',
  color: theme.palette.primary.contrastText,
  textTransform: 'none',
  borderRadius: 1,
  whiteSpace: 'nowrap',
  boxShadow: '0px 4px 10px 0px #0000000D',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    transform: 'scale(1.03)',
    backgroundColor: '#4C5B9A',
    boxShadow: '0px 0px 4px 0px #0000000D',
    border: '1px solid transparent',
  },
  '&:active': {
    backgroundColor: '#4C5B9A',
    boxShadow: '0px 0px 4px 0px rgb(55, 108, 207)',
    border: '1px solid transparent',
  },
});

const getOutlinedGradientButtonStyle = () => ({
  fontWeight: 500,
  fontSize: '0.775rem',
  padding: '0.2rem 0.5rem',
  textTransform: 'none',
  border: '1px solid rgba(37, 37, 39, 0.09)',
  color: '#5965A6',
  borderRadius: 1,
  background: '#EEF2FF',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    background: ' #4C5B9A',
    color: '#fff',
    border: '1px solid transparent',
    boxShadow: '0px 0px 4px 0px #0000000D',
  },
  '&:active': {
    backgroundColor: '#4C5B9A',
    color: '#fff',
    border: '1px solid transparent',
    boxShadow: '0px 0px 4px 0px rgb(55, 108, 207)',
  },
});

const ETLMapping = forwardRef(function ETLMapping(
  { updatedConfig, onUserEdit, onUserSave },
  ref, // eslint-disable-line no-unused-vars
) {
  const { darkMode } = useThemeContext();
  const theme = useTheme();
  const containedButtonStyle = getContainedButtonStyle(theme);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const workflowsList = useSelector((state) => state.workflows?.list || []);
  const { workflowName: workflowParam } = useParams();
  const workflowId = useMemo(() => {
    const found = workflowsList.find(
      (w) =>
        String(w.workflow_id) === String(workflowParam) ||
        w.name === workflowParam,
    );
    return found?.workflow_id || workflowParam;
  }, [workflowParam, workflowsList]);

  const [openConfirm, setOpenConfirm] = useState(false);
  const [openValidation, setOpenValidation] = useState(false);
  const [openSaveCloseDialog, setOpenSaveCloseDialog] = useState(false);

  // eslint-disable-next-line
  const [selectedEdgeIds, setSelectedEdgeIds] = useState([]);
  const [selectedNodeIds, setSelectedNodeIds] = useState([]);

  const nodeTypes = useMemo(
    () => ({
      sourceNode: SourceNode,
      destinationNode: DestinationNode,
      functionNode: (props) => (
        <FunctionNode {...props} updatedConfig={updatedConfig} />
      ),
    }),
    [updatedConfig],
  );

  const {
    nodes = [],
    edges = [],
    // connectionDetails = [],
  } = useSelector(
    (state) => state.mapping?.mappingsByWorkflow?.[workflowId] ?? {},
  );

  const mappingLoading = useSelector((state) => state.mapping?.loading);

  // Read any confirmed filterExpression stored for this workflow (for server-side save)
  const filterExpression = useSelector(
    (state) => state.mapping?.filterExpressionByWorkflow?.[workflowId],
  );

  //  const mappedFields = useSelector((state) => state.mapping?.mappedFields || {});

  const workflowIdd = useSelector((state) =>
    state.workflows.list.find((w) => w),
  );

  console.log('workflowIdd::', workflowIdd);

  const previousConnectionDetails =
    useSelector(
      (state) =>
        state.mapping?.mappingsByWorkflow?.[workflowId]?.connectionDetails,
    ) || [];

  const baseY = 150;

  const betweenNodesY = 100;

  const getEstimatedHeight = (node) => {
    const entityHeaderHeight = 48;
    const fieldRowHeight = 46;
    const nodePadding = 32;
    const totalFields = node.data.subFields?.reduce(
      (sum, group) => sum + (group.children?.length || 0),
      0,
    );
    return entityHeaderHeight + totalFields * fieldRowHeight + nodePadding;
  };

  const nodeHeights = useSelector(
    (state) => state.mapping.nodeHeightsByWorkflow?.[workflowId] || {},
  );

  // ✅ FIX: Restore mapping nodes/edges if they're empty but connectionDetails exist
  // useEffect(() => {
  //   if (
  //     !nodes.length &&
  //     !edges.length &&
  //     workflowConnectionDetails.length > 0
  //   ) {
  //     // Check if we have saved workflowJson with nodes
  //     if (workflowData?.workflowJson?.nodes?.length) {
  //       console.log(
  //         '🔄 [Mapping] Found saved nodes in workflowJson, attempting to restore...'
  //       );

  //       // Restore from workflowJson if available
  //       const savedNodes = workflowData.workflowJson.nodes;
  //       const savedEdges = workflowData.workflowJson.edges || [];

  //       // Filter for source and destination nodes (mapping-specific nodes)
  //       const mappingNodes = savedNodes.filter(
  //         (node) =>
  //           node.type === 'sourceNode' || node.type === 'destinationNode'
  //       );

  //       if (mappingNodes.length > 0) {
  //         console.log(
  //           '🔄 [Mapping] Restoring mapping nodes:',
  //           mappingNodes.length
  //         );
  //         dispatch(setNodesForWorkflow({ workflowName, nodes: mappingNodes }));
  //       }

  //       if (savedEdges.length > 0) {
  //         console.log(
  //           '🔄 [Mapping] Restoring mapping edges:',
  //           savedEdges.length
  //         );
  //         dispatch(setEdgesForWorkflow({ workflowName, edges: savedEdges }));
  //       }

  //       toast.success('Mapping visualization restored!', { autoClose: 2000 });
  //     } else {
  //       // If no saved visual state, we need to rebuild from scratch
  //       console.log(
  //         '🔄 [Mapping] No saved visual state found, may need to rebuild from entity data'
  //       );
  //       toast.info(
  //         'Mapping data found but visualization needs to be rebuilt. Please refresh the mapping view.',
  //         { autoClose: 4000 }
  //       );
  //     }
  //   }
  // }, [nodes, edges, workflowConnectionDetails, workflowName, workflowData, dispatch]);

  const mutableNodes = useMemo(
    () =>
      nodes.map((n) =>
        cloneDeepWith(n, (value) => {
          if (typeof value === 'function' || React.isValidElement(value)) {
            return value;
          }
          return undefined;
        }),
      ),
    [nodes],
  );

  console.log('mutableNodes:', mutableNodes);

  const mutableEdges = useMemo(
    () =>
      edges.map((e) =>
        cloneDeepWith(e, (value) =>
          typeof value === 'function' ? value : undefined,
        ),
      ),
    [edges],
  );

  console.log('mutableEdges::', mutableEdges);

  // eslint-disable-next-line no-unused-vars
  const [connections, setConnections] = useState([]);

  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const [openEntityDialog, setOpenEntityDialog] = useState(false);

  const lastExpandAllTrigger = useSelector(
    (state) => state?.mapping?.expandAllTriggerByWorkflow?.[workflowId] || 0,
  );
  const {
    secondaryDestinationId,
    //eslint-disable-next-line no-unused-vars
    primaryDestinationId,
    sourceName,
    // dest1Name,
    // dest2Name,
    decisionMeta,
  } = location.state || {};

  const activeDestId =
    decisionMeta?.destIds?.[decisionMeta?.currentIndex ?? 0] ??
    secondaryDestinationId ??
    null;

  const [openProceedNext, setOpenProceedNext] = useState(false);

  const dropCountRef = useRef(0);

  // const getFullConnectionDetails = () => {
  //   const result = [];

  //   const findNode = (id) => mutableNodes.find((n) => n.id === id);
  //   // const findEdgesBySource = (id) => edges.filter((e) => e.source === id);
  //   const findEdgeByTarget = (id) => mutableEdges.find((e) => e.target === id);

  //   const tracePath = (startEdge) => {
  //     let currentEdge = startEdge;
  //     let currentSource = currentEdge.source;

  //     const node = findNode(currentSource);
  //     if (!node) return null;

  //     if (
  //       node.type === 'functionNode' &&
  //       node.data.functionType === 'aggregation'
  //     ) {
  //       const inputEdges = mutableEdges.filter((e) => e.target === node.id);
  //       const allSources = inputEdges
  //         .map((inEdge) => {
  //           const sourceNode = findNode(inEdge.source);
  //           if (!sourceNode) return null;

  //           if (sourceNode.type === 'sourceNode') {
  //             return {
  //               functionName: 'aggregation',
  //               config: node.data.config,
  //               position: node.position,
  //               source: getFieldInfo(sourceNode, inEdge.sourceHandle),
  //               functions: [],
  //             };
  //           }

  //           if (sourceNode.type === 'functionNode') {
  //             const innerPath = tracePath(inEdge);
  //             return {
  //               functionName: 'aggregation',
  //               config: node.data.config,
  //               position: node.position,
  //               source: innerPath?.source,
  //               functions: [...(innerPath?.functions || [])],
  //             };
  //           }

  //           return null;
  //         })
  //         .filter(Boolean);

  //       return allSources;
  //     }

  //     if (node.type === 'functionNode') {
  //       const nextEdge = findEdgeByTarget(node.id);
  //       const nextPath = nextEdge ? tracePath(nextEdge) : null;

  //       if (nextPath && nextPath.source) {
  //         return {
  //           source: nextPath.source,
  //           functions: [
  //             ...nextPath.functions,
  //             {
  //               functionName: node.data.functionType,
  //               config: node.data.config,
  //               position: node.position,
  //             },
  //           ],
  //         };
  //       }
  //     }

  //     if (node.type === 'sourceNode') {
  //       const sourceFieldId = currentEdge.sourceHandle;
  //       const sourceInfo = getFieldInfo(node, sourceFieldId);
  //       return {
  //         source: sourceInfo,
  //         functions: [],
  //       };
  //     }

  //     return null;
  //   };

  //   const getFieldInfo = (node, fieldId) => {
  //     for (const group of node.data.subFields || []) {
  //       for (const field of group.children || []) {
  //         if (field.id === fieldId) {
  //           return {
  //             parentId: group.id,
  //             fieldId: field.id,
  //             label: field.label,
  //             type: field.type,
  //           };
  //         }
  //       }
  //     }
  //     return {};
  //   };

  //   mutableEdges.forEach((edge) => {
  //     const destNode = findNode(edge.target);
  //     if (!destNode || destNode.type !== 'destinationNode') return;

  //     const destinationFieldId = edge.targetHandle;
  //     const destInfo = getFieldInfo(destNode, destinationFieldId);

  //     const pathInfo = tracePath(edge);
  //     if (!pathInfo) return;

  //     if (Array.isArray(pathInfo)) {
  //       pathInfo.forEach((p) => {
  //         if (!p?.source) return;
  //         result.push({
  //           sourceParentId: p.source.parentId,
  //           sourceFieldId: p.source.fieldId,
  //           sourceLabel: p.source.label,
  //           type: p.source.type,
  //           destinationParentId: destInfo.parentId,
  //           destinationFieldId: destInfo.fieldId,
  //           destinationLabel: destInfo.label,
  //           destinationType: destInfo.type,
  //           functionChain: [
  //             ...(p.functions || []),
  //             {
  //               functionName: p.functionName,
  //               config: p.config,
  //             },
  //           ],
  //         });
  //       });
  //     } else {
  //       result.push({
  //         sourceParentId: pathInfo.source.parentId,
  //         sourceFieldId: pathInfo.source.fieldId,
  //         sourceLabel: pathInfo.source.label,
  //         type: pathInfo.source.type,
  //         destinationParentId: destInfo.parentId,
  //         destinationFieldId: destInfo.fieldId,
  //         destinationLabel: destInfo.label,
  //         destinationType: destInfo.type,
  //         functionChain: pathInfo.functions,
  //       });
  //     }
  //   });

  //   return result;
  // };

  useEffect(() => {
    if (!mutableNodes.length) return;

    let currentY = baseY;
    const newNodes = mutableNodes.map((node) => {
      const estHeight = getEstimatedHeight(node);

      if (node.type === 'sourceNode') {
        const nextNode = {
          ...node,
          position: { ...node.position, y: currentY },
        };
        currentY += (nodeHeights[node.id] || estHeight) + betweenNodesY;
        return nextNode;
      }
      return node;
    });

    const changed = newNodes.some(
      (n, i) => n.position.y !== nodes[i]?.position?.y,
    );
    if (changed) {
      dispatch(setNodesForWorkflow({ workflowId, nodes: newNodes }));
    }
  }, [nodeHeights, mutableNodes, workflowId, dispatch]);

  useEffect(() => {
    if (!reactFlowInstance || mutableNodes.length === 0) return;

    const sourceNode = mutableNodes.find((n) => n.type === 'sourceNode');
    if (sourceNode) {
      // You want to position the canvas so that the sourceNode appears at x=0 and y=300 in screen coords
      const targetX = -sourceNode.position.x - 80;
      const targetY = -sourceNode.position.y + 100;

      reactFlowInstance.setViewport(
        {
          x: targetX,
          y: targetY,
          // zoom,
          zoom: 0.7,
        },
        {
          duration: 600, // Smooth animation
        },
      );
    }
  }, [reactFlowInstance]);

  const getFullConnectionDetails = () => {
    const result = [];

    const findNode = (id) => mutableNodes.find((n) => n.id === id);
    const findEdgeByTarget = (id) => mutableEdges.find((e) => e.target === id);

    const getFieldInfo = (node, fieldId) => {
      for (const group of node.data.subFields || []) {
        for (const field of group.children || []) {
          if (field.id === fieldId) {
            console.log(
              `Checking field ${field.label} (id=${field.id}) — isMismatch:`,
              field.isMismatch,
            );
            return {
              parentId: group.id,
              fieldId: field.id,
              label: field.label,
              type: field.type,
              isMapped: field.isMapped || false,
              isMismatch: field.isMismatch || false,
            };
          }
        }
      }
      return {};
    };

    const tracePath = (startEdge) => {
      let currentEdge = startEdge;
      let currentSource = currentEdge.source;
      const node = findNode(currentSource);
      if (!node) return null;

      if (
        node.type === 'functionNode' &&
        node.data.functionType === 'aggregation'
      ) {
        const inputEdges = mutableEdges.filter((e) => e.target === node.id);
        const allSources = inputEdges
          .map((inEdge) => {
            const sourceNode = findNode(inEdge.source);
            if (!sourceNode) return null;
            if (sourceNode.type === 'sourceNode') {
              const srcHandle = getActualHandle(inEdge, 'source');
              const srcInfo = getFieldInfo(sourceNode, srcHandle);
              if (!srcInfo?.fieldId) return null;

              return {
                source: srcInfo,
                // source: getFieldInfo(sourceNode, inEdge.sourceHandle),
                // source: getFieldInfo(sourceNode, getActualHandle(inEdge, 'source')),
                functions: [],
                edgeMeta: inEdge.data || {},
              };
            }

            if (sourceNode.type === 'functionNode') {
              const innerPath = tracePath(inEdge);
              if (!innerPath?.source?.fieldId) return null;
              return {
                source: innerPath?.source,
                functions: [...(innerPath?.functions || [])],
                edgeMeta: inEdge.data || {},
              };
            }

            return null;
          })
          .filter(Boolean);

        // Return optimized aggregation structure with all source fields
        return {
          functionName: 'aggregation',
          config: node.data.config,
          position: node.position,
          sourceFields: allSources,
          acceptedTypes: functionTypeCompatibility['aggregation'] || [],
          isAggregation: true,
        };
      }

      if (node.type === 'functionNode') {
        const nextEdge = findEdgeByTarget(node.id);
        const nextPath = nextEdge ? tracePath(nextEdge) : null;

        if (nextPath && nextPath.source) {
          return {
            source: nextPath.source,
            functions: [
              ...nextPath.functions,
              {
                functionName: node.data.functionType,
                config: node.data.config,
                position: node.position,
                acceptedTypes:
                  functionTypeCompatibility[node.data.functionType] || [],
              },
            ],
            edgeMeta: currentEdge.data || {},
          };
        }
      }

      if (node.type === 'sourceNode') {
        // const sourceFieldId = currentEdge.sourceHandle;
        const sourceFieldId = getActualHandle(currentEdge, 'source');
        const sourceInfo = getFieldInfo(node, sourceFieldId);
        return {
          source: sourceInfo,
          functions: [],
          edgeMeta: currentEdge.data || {},
        };
      }

      return null;
    };

    mutableEdges.forEach((edge) => {
      const destNode = findNode(edge.target);
      if (!destNode || destNode.type !== 'destinationNode') return;

      // const destinationFieldId = edge.targetHandle;
      const destinationFieldId = getActualHandle(edge, 'target');
      const destInfo = getFieldInfo(destNode, destinationFieldId);
      const pathInfo = tracePath(edge);
      if (!pathInfo) return;

      if (pathInfo?.isAggregation) {
        // Handle optimized aggregation structure
        const functionChain = [
          {
            functionName: pathInfo.functionName,
            config: pathInfo.config,
            position: pathInfo.position,
          },
        ];

        // const getCleanFieldName = (fieldId) => {
        //   if (fieldId.includes('__')) {
        //     return fieldId.split('__').pop();
        //   }
        //   return fieldId;
        // };

        const getCleanFieldName = (fieldId) => {
          if (typeof fieldId !== 'string') return '';
          return fieldId.includes('__') ? fieldId.split('__').pop() : fieldId;
        };

        // const sourceFieldIds = pathInfo.sourceFields
        //   .map((sf) => getCleanFieldName(sf.source.fieldId))
        //   .join(',');

        const sourceFieldIds = pathInfo.sourceFields
          .map((sf) => getCleanFieldName(sf?.source?.fieldId))
          .filter(Boolean)
          .join(',');

        const sourceLabels = pathInfo.sourceFields
          .map((sf) => sf?.source?.label)
          .filter(Boolean)
          .join(',');
        const sourceParentId = pathInfo.sourceFields[0]?.source.parentId || '';
        const sourceType = pathInfo.sourceFields[0]?.source.type || '';

        const aggregatedMapping = {
          destinationNodeId: destInfo && destNode ? destNode.id : undefined,
          sourceParentId: sourceParentId,
          sourceFieldId: sourceFieldIds,
          sourceLabel: sourceLabels,
          type: sourceType,
          destinationParentId: destInfo.parentId,
          destinationFieldId: destInfo.fieldId,
          destinationLabel: destInfo.label,
          destinationType: destInfo.type,
          functionChain: functionChain,
          mappingMeta: {
            isFuzzy: false,
            isManual: true,
            isFunctionEdge: true,
            isValid: true,
            isAggregation: true,
          },
          // Additional metadata for backend processing
          aggregationSourceFields: pathInfo.sourceFields.map((sf) => ({
            fieldId: getCleanFieldName(sf.source.fieldId),
            label: sf.source.label,
            parentId: sf.source.parentId,
            type: sf.source.type,
          })),
        };

        result.push(aggregatedMapping);
      } else if (Array.isArray(pathInfo)) {
        pathInfo.forEach((p) => {
          if (!p?.source) return;

          // Build finalStep including position if available
          const finalStep = {
            functionName: p.functionName,
            config: p.config,
            ...(p.position ? { position: p.position } : {}),
          };

          const fullFunctionChain = [...(p.functions || []), finalStep];

          const isFuzzy = p.edgeMeta?.isFuzzy || false;
          const isManual = p.edgeMeta?.isManual || false;
          const hasFunctions = fullFunctionChain.length > 0;

          result.push({
            destinationNodeId: destInfo && destNode ? destNode.id : undefined,
            sourceParentId: p.source.parentId,
            sourceFieldId: p.source.fieldId,
            sourceLabel: p.source.label,
            type: p.source.type,
            destinationParentId: destInfo.parentId,
            destinationFieldId: destInfo.fieldId,
            destinationLabel: destInfo.label,
            destinationType: destInfo.type,
            functionChain: fullFunctionChain,
            mappingMeta: {
              isFuzzy,
              isManual,
              isFunctionEdge: hasFunctions,
              isValid: isFuzzy
                ? !p.source?.isMismatch
                : isManual
                  ? p.edgeMeta?.isValid === true
                  : false,
            },
          });
        });
      } else {
        const isFuzzy = pathInfo.edgeMeta?.isFuzzy || false;
        const isManual = pathInfo.edgeMeta?.isManual || false;
        const hasFunctions = (pathInfo.functions || []).length > 0;

        result.push({
          destinationNodeId: destInfo && destNode ? destNode.id : undefined,
          sourceParentId: pathInfo.source.parentId,
          sourceFieldId: pathInfo.source.fieldId,
          sourceLabel: pathInfo.source.label,
          type: pathInfo.source.type,
          destinationParentId: destInfo.parentId,
          destinationFieldId: destInfo.fieldId,
          destinationLabel: destInfo.label,
          destinationType: destInfo.type,
          functionChain: pathInfo.functions,
          mappingMeta: {
            isFuzzy,
            isManual,
            isFunctionEdge: hasFunctions,
            isValid: isFuzzy
              ? !pathInfo.source?.isMismatch
              : isManual
                ? pathInfo.edgeMeta?.isValid === true
                : false,
          },
        });
      }
    });

    return result;
  };
  console.log('new nodes nodes:', mutableNodes);
  const handleConfirmDiscard = () => {
    onUserEdit?.();

    dispatch(setEdgesForWorkflow({ workflowId, edges: [] }));

    function clearMappingColors(nodes) {
      return nodes.map((node) => {
        if (!node.data?.subFields) return node;
        return {
          ...node,
          data: {
            ...node.data,
            subFields: node.data.subFields.map((group) => ({
              ...group,
              children: group.children.map((child) => {
                const newChild = { ...child };
                delete newChild.color;
                newChild.isMapped = false;
                newChild.isMismatch = false;
                return newChild;
              }),
            })),
          },
        };
      });
    }

    const filteredNodes = clearMappingColors(
      mutableNodes.filter(
        (n) => n.type === 'sourceNode' || n.type === 'destinationNode',
      ),
    );
    dispatch(setNodesForWorkflow({ workflowId, nodes: filteredNodes }));
    dispatch(
      setConnectionDetailsForWorkflow({ workflowId, connectionDetails: [] }),
    );

    dispatch(clearFuzzyStateForWorkflow({ workflowId }));

    setOpenConfirm(false);
    toast.success('All mappings discarded.', { autoClose: 2000 });
  };

  // const handleSave = () => {
  //   const connectionDetails = getFullConnectionDetails();
  //   console.log('First saved Connection Data:', connectionDetails);

  //   dispatch(setNodesForWorkflow({ workflowName, nodes: mutableNodes }));
  //   dispatch(setEdgesForWorkflow({ workflowName, edges: mutableEdges }));
  //   dispatch(
  //     setConnectionDetailsForWorkflow({ workflowName, connectionDetails })
  //   );

  //   // setIsSaved(true);
  //   toast.success('Workflow saved successfully', { autoClose: 3000 });
  // };

  // const handleCancel = () => {
  //   navigate(`/parentworkflow/${workflowName}`);
  // };

  const pickActiveDestination = (nodes, edges) => {
    const destNodes = nodes.filter((n) => n.type === 'destinationNode');
    if (destNodes.length === 0) return null;
    if (destNodes.length === 1) return destNodes[0];

    const counts = new Map(destNodes.map((n) => [n.id, 0]));
    for (const e of edges) {
      if (counts.has(e.target)) counts.set(e.target, counts.get(e.target) + 1);
    }
    const bestId = [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
    return destNodes.find((n) => n.id === bestId) || destNodes[0];
  };

  //   const handleSaveClose = async () => {
  //     dispatch(
  //       setExpandAllTrigger({
  //         workflowName,
  //         trigger: lastExpandAllTrigger + 1,
  //       })
  //     );

  //     // await new Promise((res) => setTimeout(res, 0));

  //     if (mutableEdges.length === 0) {
  //       setOpenValidation(true);
  //       return;
  //     }

  //     const hasInvalidEdges = mutableEdges.some(
  //       (edge) =>
  //         edge.data?.isValid === false ||
  //         (edge.data?.isFuzzy && edge.data?.sourceType !== edge.data?.targetType)
  //     );

  //     if (hasInvalidEdges) {
  //       toast.error(
  //         'Mismatched edges. Please remove or correct them before saving.',
  //         { autoClose: 4000 }
  //       );
  //       return;
  //     }

  //     // const destinationNode = mutableNodes.find(
  //     //   (n) => n.type === 'destinationNode'
  //     // );
  //     // console.log('destinationNode in save:', destinationNode);
  //     // console.log('destinationNode.id:', destinationNode?.id);

  //     // const allDestFields =
  //     //   destinationNode?.data?.subFields?.flatMap((group) => group.children) ||
  //     //   [];

  //     // console.log('all dest fields:', allDestFields);

  //     // const requireFields = allDestFields.filter(
  //     //   (f) => f.is_mandatory === true && f.is_creatable === true
  //     // );

  //     // console.log('requireFields:', requireFields);

  //     // // const unmappedRequired = requireFields.filter((field) => {
  //     // //   return !mutableEdges.some(
  //     // //     (edge) =>
  //     // //       edge.target === destinationNode.id && edge.targetHandle === field.id
  //     // //   );
  //     // // });

  //     // console.log('mutableEdges:::', JSON.stringify(mutableEdges, null, 2));

  //     // // new update working
  //     // const unmappedRequired = requireFields.filter((field) => {
  //     //   return !mutableEdges.some((edge) => {
  //     //     const matches =
  //     //       edge.target === destinationNode.id &&
  //     //       (edge.targetHandle === field.id || edge.data?._prev === field.id) &&
  //     //       edge.data?.isValid === true &&
  //     //       ((edge.data.isFuzzy === true && edge.data.isManual === false) ||
  //     //         (edge.data.isManual === true && edge.data.isFuzzy === false));
  //     //     if (matches) {
  //     //       console.log(
  //     //         'Mapped required field:',
  //     //         field.label,
  //     //         'with edge:',
  //     //         edge
  //     //       );
  //     //     }
  //     //     return matches;
  //     //   });
  //     // });

  //     // console.log(
  //     //   'Unmapped required fields (should trigger toast):',
  //     //   unmappedRequired
  //     // );

  //     // console.log('unmappedRequired:', unmappedRequired);

  //     // if (unmappedRequired.length > 0) {
  //     //   toast.error(
  //     //     `Missing required fields: ${unmappedRequired
  //     //       .map((f) => f.label)
  //     //       .join(', ')}`,
  //     //     { autoClose: 3000 }
  //     //   );
  //     //   return;
  //     // }

  //     // const connectionDetails = getFullConnectionDetails();
  //     // console.log('Saving Connection Details:', connectionDetails);

  //     // dispatch(setNodesForWorkflow({ workflowName, nodes: mutableNodes }));
  //     // dispatch(setEdgesForWorkflow({ workflowName, edges: mutableEdges }));

  //     // // Build a fast lookup of the current destination's field ids
  //     // const thisDestFieldIdSet = new Set((allDestFields || []).map((f) => f.id));

  //     // // 1) Keep only previous rows that do NOT belong to the current destination
  //     // const previousExceptThisDest = (previousConnectionDetails || []).filter(
  //     //   (m) => !thisDestFieldIdSet.has(m.destinationFieldId)
  //     // );

  //     // const keyOf = (m) =>
  //     //   `${m.destinationParentId}::${m.destinationFieldId}::${m.sourceParentId}::${m.sourceFieldId}`;

  //     // // 3) Merge previous (other destinations) + current (this destination) and de-dupe
  //     // const mergedMap = new Map();
  //     // [...previousExceptThisDest, ...connectionDetails].forEach((m) => {
  //     //   mergedMap.set(keyOf(m), m);
  //     // });
  //     // const mergedConnectionDetails = Array.from(mergedMap.values());
  // //----------------------------------------------------------------------------
  //      // --- NEW: detect # of destinations and pick active one if needed
  //   const destNodes = mutableNodes.filter((n) => n.type === 'destinationNode');
  //   const hasTwoDestinations = destNodes.length > 1;

  //   const destinationNode = hasTwoDestinations
  //     ? pickActiveDestination(mutableNodes, mutableEdges)
  //     : destNodes[0] || null;

  //   if (!destinationNode) {
  //     toast.error('No destination found for saving.');
  //     return;
  //   }

  //   console.log('destinationNode in save:', destinationNode);
  //   console.log('destinationNode.id:', destinationNode?.id);

  //   // Required-field validation (always scoped to the destination we're saving)
  //   const allDestFields =
  //     destinationNode?.data?.subFields?.flatMap((group) => group.children) || [];
  //   const requireFields = allDestFields.filter(
  //     (f) => f.is_mandatory === true && f.is_creatable === true
  //   );

  //   const unmappedRequired = requireFields.filter((field) => {
  //     return !mutableEdges.some((edge) => {
  //       const matches =
  //         edge.target === destinationNode.id &&
  //         (edge.targetHandle === field.id || edge.data?._prev === field.id) &&
  //         edge.data?.isValid === true &&
  //         ((edge.data.isFuzzy === true && edge.data.isManual === false) ||
  //           (edge.data.isManual === true && edge.data.isFuzzy === false));
  //       return matches;
  //     });
  //   });

  //   if (unmappedRequired.length > 0) {
  //     toast.error(
  //       `Missing required fields: ${unmappedRequired.map((f) => f.label).join(', ')}`,
  //       { autoClose: 3000 }
  //     );
  //     return;
  //   }

  //   const connectionDetails = getFullConnectionDetails(); // now rows include destinationNodeId
  //   console.log('Saving Connection Details:', connectionDetails);

  //   dispatch(setNodesForWorkflow({ workflowName, nodes: mutableNodes }));
  //   dispatch(setEdgesForWorkflow({ workflowName, edges: mutableEdges }));

  //   // ---------------------------
  //   // MERGE STRATEGY:
  //   // - If ONLY ONE destination on canvas → keep your original simple logic
  //   // - If TWO+ destinations → scope merge by destinationNodeId (append/patch only this mapping)
  //   // ---------------------------

  //   let mergedConnectionDetails;

  //   if (!hasTwoDestinations) {
  //     // === Original (single-destination) behavior ===
  //     const thisDestFieldIdSet = new Set((allDestFields || []).map((f) => f.id));
  //     const previousExceptThisDest = (previousConnectionDetails || []).filter(
  //       (m) => !thisDestFieldIdSet.has(m.destinationFieldId)
  //     );

  //     console.log('unmappedRequired:', unmappedRequired);

  //     if (unmappedRequired.length > 0) {
  //       toast.error(
  //         `Missing required fields: ${unmappedRequired
  //           .map((f) => f.label)
  //           .join(', ')}`,
  //         { autoClose: 3000 }
  //       );
  //       return;
  //     }

  //     const connectionDetails = getFullConnectionDetails();
  //     console.log('Saving Connection Details:', connectionDetails);

  //     dispatch(setNodesForWorkflow({ workflowName, nodes: mutableNodes }));
  //     dispatch(setEdgesForWorkflow({ workflowName, edges: mutableEdges }));

  //     // Updated by Vishal for one to many

  //     // const isOneToMany = !!secondaryDestinationId;

  //     // console.log('isOneToMany::', isOneToMany)

  //     // ✅ Use ONLY these four IDs for uniqueness (no __destinationId)
  //     const keyOf = (m) =>
  //       `${m.destinationParentId}::${m.destinationFieldId}::${m.sourceParentId}::${m.sourceFieldId}`;

  //     const mergedMap = new Map();
  //     [...previousExceptThisDest, ...connectionDetails].forEach((m) => {
  //       mergedMap.set(keyOf(m), m);
  //     });
  //     mergedConnectionDetails = Array.from(mergedMap.values());
  //   } else {
  //     // === New (multi-destination) behavior: scope by destinationNodeId ===
  //     const activeDestId = destinationNode.id;

  //     // Separate previous rows that do NOT belong to the active destination.
  //     // Backward compatibility: if older rows lack destinationNodeId, we fall back
  //     // to checking if their destinationFieldId belongs to this destination.
  //     let previousExceptThisDest = (previousConnectionDetails || []).filter(
  //       (m) => m.destinationNodeId !== activeDestId
  //     );

  //     // Updated by Vishal for one to many and conditions are added so it can work for one to many, many to one and single to single
  //     // const base = isOneToMany ? previousConnectionDetails || [] : [];
  //     // [...base, ...connectionDetails].forEach((m) => mergedMap.set(keyOf(m), m));
  //     // const payloadMappings = isOneToMany
  //     //   ? Array.from(mergedMap.values())
  //     //   : connectionDetails;

  //     // dispatch(
  //     //   setConnectionDetailsForWorkflow({
  //     //     workflowName,
  //     //     connectionDetails: payloadMappings,
  //     //   })
  //     // );

  //     if ((previousConnectionDetails || []).some((m) => !m.destinationNodeId)) {
  //       const thisDestFieldIdSet = new Set((allDestFields || []).map((f) => f.id));
  //       previousExceptThisDest = (previousConnectionDetails || []).filter((m) => {
  //         if (m.destinationNodeId) return m.destinationNodeId !== activeDestId;
  //         // legacy row: remove if its destination field belongs to this destination
  //         return !thisDestFieldIdSet.has(m.destinationFieldId);
  //       });
  //     }

  //     // Keep only the rows built for the active destination in this save
  //     const currentForThisDest = connectionDetails.filter(
  //       (m) => m.destinationNodeId === activeDestId
  //     );

  //     // Dedupe key now includes destinationNodeId to avoid cross-mapping collisions
  //     const keyOf = (m) =>
  //       `${m.destinationNodeId || activeDestId}::${m.destinationParentId}::${m.destinationFieldId}::${m.sourceParentId}::${m.sourceFieldId}`;

  //     const mergedMap = new Map();
  //     [...previousExceptThisDest, ...currentForThisDest].forEach((m) => {
  //       mergedMap.set(keyOf(m), m);
  //     });
  //     mergedConnectionDetails = Array.from(mergedMap.values());
  //   }
  //     // Persist merged result into Redux
  //     dispatch(
  //       setConnectionDetailsForWorkflow({
  //         workflowName,
  //         connectionDetails: mergedConnectionDetails,
  //       })
  //     );

  //     // Updated by Vishal for one to many
  //     try {
  //       await dispatch(
  //         saveMappingThunk({
  //           workflow_id: workflowId,
  //           mapping_json: {
  //             mappings: mergedConnectionDetails,
  //           },
  //         })
  //       ).unwrap();

  //       toast.success('Final Workflow saved successfully', { autoClose: 3000 });
  //       dispatch(
  //         setBlinkByWorkflow({ workflowName, button: 'save', value: true })
  //       );
  //       dispatch(
  //         setBlinkByWorkflow({ workflowName, button: 'execute', value: false })
  //       );

  //       onUserSave?.();
  //       if (secondaryDestinationId) {
  //         setOpenProceedNext(true);
  //       } else {
  //         setTimeout(() => {
  //           navigate(`/parentworkflow/${workflowName}`);
  //         }, 2000);
  //       }
  //     } catch (err) {
  //       console.error('Failed to save mapping:', err);
  //       toast.error('Failed to save mapping');
  //     }
  //   };

  const handleSaveClose = async () => {
    dispatch(
      setExpandAllTrigger({ workflowId, trigger: lastExpandAllTrigger + 1 }),
    );

    if (mutableEdges.length === 0) {
      setOpenValidation(true);
      return;
    }

    const hasInvalidEdges = mutableEdges.some(
      (edge) =>
        edge.data?.isValid === false ||
        (edge.data?.isFuzzy && edge.data?.sourceType !== edge.data?.targetType),
    );
    if (hasInvalidEdges) {
      toast.error(
        'Mismatched edges. Please remove or correct them before saving.',
        { autoClose: 4000 },
      );
      return;
    }

    const destNodes = mutableNodes.filter((n) => n.type === 'destinationNode');
    const isDecisionFlow = !!(decisionMeta?.destIds?.length > 1);
    const multiDestMode = isDecisionFlow && destNodes.length > 1;

    const forcedDestId =
      decisionMeta?.destIds?.[decisionMeta.currentIndex] ?? null;
    const destinationNode = forcedDestId
      ? destNodes.find((n) => n.id === forcedDestId) || destNodes[0] || null
      : multiDestMode
        ? pickActiveDestination(mutableNodes, mutableEdges)
        : destNodes[0] || null;

    if (!destinationNode) {
      toast.error('No destination found for saving.');
      return;
    }

    const allDestFields =
      destinationNode?.data?.subFields?.flatMap((g) => g.children) || [];
    const requireFields = allDestFields.filter(
      (f) => f.is_mandatory === true && f.is_creatable === true,
    );

    const unmappedRequired = requireFields.filter(
      (field) =>
        !mutableEdges.some((edge) => {
          const matches =
            edge.target === destinationNode.id &&
            (edge.targetHandle === field.id || edge.data?._prev === field.id) &&
            edge.data?.isValid === true &&
            ((edge.data.isFuzzy === true && edge.data.isManual === false) ||
              (edge.data.isManual === true && edge.data.isFuzzy === false));
          return matches;
        }),
    );
    if (unmappedRequired.length > 0) {
      toast.error(
        `Missing required fields: ${unmappedRequired
          .map((f) => f.label)
          .join(', ')}`,
        { autoClose: 3000 },
      );
      return;
    }

    const connectionDetails = getFullConnectionDetails();
    dispatch(setNodesForWorkflow({ workflowId, nodes: mutableNodes }));
    dispatch(setEdgesForWorkflow({ workflowId, edges: mutableEdges }));

    let mergedConnectionDetails;

    if (!multiDestMode) {
      const thisDestFieldIdSet = new Set(
        (allDestFields || []).map((f) => f.id),
      );
      const previousExceptThisDest = (previousConnectionDetails || []).filter(
        (m) => !thisDestFieldIdSet.has(m.destinationFieldId),
      );

      const keyOf = (m) =>
        `${m.destinationParentId}::${m.destinationFieldId}::${m.sourceParentId}::${m.sourceFieldId}`;

      const mergedMap = new Map();
      [...previousExceptThisDest, ...connectionDetails].forEach((m) =>
        mergedMap.set(keyOf(m), m),
      );
      mergedConnectionDetails = Array.from(mergedMap.values());
    } else {
      const activeDestId = destinationNode.id;

      let previousExceptThisDest = (previousConnectionDetails || []).filter(
        (m) => m.destinationNodeId !== activeDestId,
      );

      if ((previousConnectionDetails || []).some((m) => !m.destinationNodeId)) {
        const thisDestFieldIdSet = new Set(
          (allDestFields || []).map((f) => f.id),
        );
        previousExceptThisDest = (previousConnectionDetails || []).filter(
          (m) => {
            if (m.destinationNodeId)
              return m.destinationNodeId !== activeDestId;
            return !thisDestFieldIdSet.has(m.destinationFieldId);
          },
        );
      }

      const currentForThisDest = connectionDetails.filter(
        (m) => m.destinationNodeId === activeDestId,
      );

      const keyOf = (m) =>
        `${m.destinationNodeId || activeDestId}::${m.destinationParentId}::${
          m.destinationFieldId
        }::${m.sourceParentId}::${m.sourceFieldId}`;

      const mergedMap = new Map();
      [...previousExceptThisDest, ...currentForThisDest].forEach((m) =>
        mergedMap.set(keyOf(m), m),
      );
      mergedConnectionDetails = Array.from(mergedMap.values());
    }

    dispatch(
      setConnectionDetailsForWorkflow({
        workflowId,
        connectionDetails: mergedConnectionDetails,
      }),
    );

    try {
      await dispatch(
        saveMappingThunk({
          workflow_id: workflowId,
          mapping_json: {
            mappings: mergedConnectionDetails,
            // Persist filterExpression to backend (if present)
            filterExpression: filterExpression || null,
          },
        }),
      ).unwrap();

      // toast.success('Final Workflow saved successfully', { autoClose: 3000 });
      dispatch(setBlinkByWorkflow({ workflowId, button: 'save', value: true }));
      dispatch(
        setBlinkByWorkflow({ workflowId, button: 'execute', value: false }),
      );

      onUserSave?.();

      // if (secondaryDestinationId) {
      //   console.log('next?', secondaryDestinationId, decisionMeta);
      //   setOpenProceedNext(true);
      // } else {
      //   setTimeout(() => navigate(`/parentworkflow/${workflowName}`), 2000);
      // }

      // const hasNextViaMeta = decisionMeta?.destIds?.length
      //   ? (decisionMeta.currentIndex ?? 0) + 1 < decisionMeta.destIds.length
      //   : false;

      const total = decisionMeta?.destIds?.length || 0;
      const idx = decisionMeta?.currentIndex ?? 0;
      const hasNextViaMeta = total ? idx + 1 < total : false;

      if (hasNextViaMeta || secondaryDestinationId) {
        const nextLabel =
          decisionMeta?.destNames?.[idx + 1] ??
          decisionMeta?.destIds?.[idx + 1] ??
          'next destination';

        const stepMsg =
          total > 0
            ? `Step ${idx + 1}/${total} saved. Proceeding to ${nextLabel}…`
            : `Saved. Proceeding to ${nextLabel}…`;

        toast.success(stepMsg, {
          autoClose: 1500,
        });
      } else {
        toast.success('Final Mapping saved successfully', { autoClose: 2500 });
      }

      if (hasNextViaMeta || secondaryDestinationId) {
        setOpenProceedNext(true);
      } else {
        setTimeout(() => {
          navigate(`/parentworkflow/${workflowId}`);
        }, 2000);
      }
    } catch (err) {
      console.error('Failed to save mapping:', err);
      toast.error('Failed to save mapping');
    }
  };

  const onConnect = useCallback(
    (connection) => {
      console.log('New Connection:', connection);

      onUserEdit?.();

      const sourceNode = mutableNodes.find((n) => n.id === connection.source);
      const targetNode = mutableNodes.find((n) => n.id === connection.target);

      const shouldBlockFromFunctionNode = (nodeId) => {
        return mutableEdges.some((e) => e.target === nodeId && e.data?.invalid);
      };

      const isDirectSourceToDest =
        sourceNode?.type === 'sourceNode' &&
        targetNode?.type === 'destinationNode';

      let isValidType = true;
      let sourceType = 'unknown';
      let targetType = 'unknown';

      const sourceHandleId = connection.sourceHandle?.split('--')[0];
      const targetHandleId = connection.targetHandle?.split('--')[0];

      const sourceFuncType = sourceNode?.data?.functionType;
      const targetFuncType = targetNode?.data?.functionType;

      const sourceIsAggregator =
        sourceNode?.type === 'functionNode' && sourceFuncType === 'aggregation';
      const sourceIsSplit =
        sourceNode?.type === 'functionNode' && sourceFuncType === 'field_split';

      const targetIsAggregator =
        targetNode?.type === 'functionNode' && targetFuncType === 'aggregation';
      // const targetIsSplit =
      //   targetNode?.type === 'functionNode' && targetFuncType === 'split';

      // const isSourceFieldAlreadyConnected = mutableEdges.some(
      //   (e) =>
      //     e.source === connection.source &&
      //     e.sourceHandle === connection.sourceHandle
      // );

      const isTargetFieldAlreadyConnected = mutableEdges.some(
        (e) =>
          e.target === connection.target &&
          e.targetHandle === connection.targetHandle,
      );

      // if (!sourceIsSplit && isSourceFieldAlreadyConnected) {
      //   toast.error(
      //     'This source field is already connected to a destination field.'
      //   );
      //   return;
      // }

      if (!targetIsAggregator && isTargetFieldAlreadyConnected) {
        toast.error(
          'This destination field is already connected to a source field.',
        );
        return;
      }

      if (sourceNode?.data?.subFields) {
        for (const group of sourceNode.data.subFields) {
          const found = group.children?.find(
            (field) => field.id === sourceHandleId,
          );
          if (found) {
            sourceType = found.type || 'unknown';
            break;
          }
        }
      }

      if (targetNode?.data?.subFields) {
        for (const group of targetNode.data.subFields) {
          const found = group.children?.find(
            (field) => field.id === targetHandleId,
          );
          if (found) {
            targetType = found.type || 'unknown';
            break;
          }
        }
      }

      // const normSourceType = normalizeType(sourceType);
      // const normTargetType = normalizeType(targetType);

      const sourceTypeAliases = getAllTypeAliases(sourceType);
      const targetTypeAliases = getAllTypeAliases(targetType);

      const floatTypes = ['float', 'double'];

      const sourceHasFloat = sourceTypeAliases.some((type) =>
        floatTypes.includes(type),
      );
      const targetIsNumber = targetTypeAliases.includes('number');

      if (isDirectSourceToDest && sourceHasFloat && targetIsNumber) {
        isValidType = false;
        toast.error(
          'Mapping float/double to number may lose decimals and is not allowed.',
        );
        return;
      }

      if (
        sourceNode?.type === 'sourceNode' &&
        targetNode?.type === 'functionNode' &&
        sourceHasFloat &&
        targetIsNumber
      ) {
        isValidType = false;
        toast.error(
          'Mapping float/double to number (function input) may lose decimals and is not allowed.',
        );
        return;
      }

      if (
        sourceNode?.type === 'functionNode' &&
        targetNode?.type === 'destinationNode' &&
        sourceHasFloat &&
        targetIsNumber
      ) {
        isValidType = false;
        toast.error(
          'Mapping float/double to number (function output) may lose decimals and is not allowed.',
        );
        return;
      }

      if (
        isDirectSourceToDest &&
        sourceType !== 'unknown' &&
        targetType !== 'unknown'
      ) {
        // if (sourceType !== targetType) {
        //   isValidType = false;
        // }

        // if (normSourceType !== normTargetType) {
        //   isValidType = false;
        // }

        const hasTypeIntersection = sourceTypeAliases.some((type) =>
          targetTypeAliases.includes(type),
        );
        if (!hasTypeIntersection) {
          isValidType = false;
        }
      }

      if (
        sourceNode?.type === 'sourceNode' &&
        targetNode?.type === 'functionNode'
      ) {
        const functionType = targetNode.data?.functionType;
        const expectedTypes = functionTypeCompatibility[functionType] || [];

        // Enhanced validation for parse_int and parse_double functions
        if (
          (functionType === 'parse_int' || functionType === 'parse_double') &&
          sourceType === 'string'
        ) {
          const sourceFieldObj = sourceNode.data.subFields
            ?.flatMap((group) => group.children)
            ?.find((field) => field.id === sourceHandleId);

          if (!isNumericStringField(sourceFieldObj)) {
            isValidType = false;
            toast.error(
              `${functionType} requires string fields that contain numeric values. Field "${
                sourceFieldObj?.label || 'Unknown'
              }" may not contain numbers.`,
              { autoClose: 5000 },
            );
            return;
          }
        } else {
          const srcAliases = getAllTypeAliases(sourceType);
          const matches = expectedTypes.some((type) =>
            srcAliases.includes(type),
          );
          if (!matches) isValidType = false;
        }
      }

      if (
        sourceNode?.type === 'functionNode' &&
        targetNode?.type === 'destinationNode'
      ) {
        const functionType = sourceNode.data?.functionType;
        const expectedOutputTypes = functionOutputTypes[functionType] || [];
        if (
          !expectedOutputTypes.includes('any') &&
          !expectedOutputTypes.some((type) => targetTypeAliases.includes(type))
        ) {
          isValidType = false;
        }
      }

      if (targetNode?.type === 'functionNode') {
        const funcType = targetNode.data?.functionType;
        const isAggregator = funcType === 'aggregation';

        const existingInputs = mutableEdges.filter(
          (e) => e.target === connection.target,
        );

        if (!isAggregator && existingInputs.length > 0) {
          console.warn(`${funcType} function node can only have one input.`);
          return;
        }
      }

      if (targetNode?.type === 'destinationNode') {
        const sourceIsAggregator =
          sourceNode?.type === 'functionNode' &&
          sourceNode.data?.functionType === 'aggregation';

        const hasEdgeToSameHandle = mutableEdges.some(
          (e) =>
            e.target === connection.target &&
            e.targetHandle === connection.targetHandle,
        );

        if (hasEdgeToSameHandle && !sourceIsAggregator) {
          console.warn('Destination field already has input on this handle');
          return;
        }
      }

      if (
        sourceNode?.type === 'functionNode' &&
        sourceNode.data?.functionType === 'aggregation'
      ) {
        const outgoing = mutableEdges.filter((e) => e.source === sourceNode.id);
        if (outgoing.length >= 1) {
          console.warn('Aggregator can only connect to one destination field.');
          return;
        }
      }

      if (
        sourceNode?.type === 'functionNode' &&
        !sourceIsAggregator &&
        !sourceIsSplit
      ) {
        const outgoing = mutableEdges.filter((e) => e.source === sourceNode.id);
        if (outgoing.length >= 1) {
          console.warn(
            `${sourceNode.data?.functionType} can only connect to one destination field.`,
          );
          return;
        }
      }

      if (
        sourceNode?.type === 'functionNode' &&
        shouldBlockFromFunctionNode(sourceNode.id)
      ) {
        console.warn(
          'Blocked: cannot connect from functionNode with invalid input',
        );
        return;
      }

      // const edgeColor = !isValidType
      //   ? 'red'
      //   : isDirectSourceToDest
      //   ? darkMode
      //     ? '#909090'
      //     : '#707070'
      //   : theme.palette.primary.main;

      const edgeColor = !isValidType
        ? 'red'
        : isDirectSourceToDest
          ? 'green'
          : theme.palette.primary.main;

      const edgeStyle = isDirectSourceToDest
        ? { strokeDasharray: 'none', strokeWidth: 1.5, stroke: edgeColor }
        : { strokeWidth: 2, stroke: edgeColor };

      const markerEnd = {
        type: 'arrowclosed',
        color: edgeColor,
      };

      const newEdges = addEdge(
        {
          ...connection,
          type: 'custom',
          data: {
            label: !isValidType ? 'Type Mismatch' : 'Valid',
            isValid: isValidType,
            sourceType,
            targetType,
            isFuzzy: false,
            isManual: true,
            functionSourceType:
              sourceNode?.type === 'functionNode'
                ? sourceNode.data?.functionType
                : null,
            functionTargetType:
              targetNode?.type === 'functionNode'
                ? targetNode.data?.functionType
                : null,
          },
          style: edgeStyle,
          animated: isDirectSourceToDest,
          markerEnd,
        },
        mutableEdges,
      );
      dispatch(setEdgesForWorkflow({ workflowId, edges: newEdges }));

      setConnections((prev) => [...prev, connection]);

      const newNodes = mutableNodes.map((node) => {
        if (node.id === connection.target) {
          return {
            ...node,
            data: {
              ...node.data,
              connections: [...(node.data.connections || []), connection],
              ...(targetNode.type === 'functionNode' && !isValidType
                ? { hasInvalidInput: true }
                : {}),
            },
          };
        }
        return node;
      });
      dispatch(setNodesForWorkflow({ workflowId, nodes: newNodes }));
    },
    [
      mutableNodes,
      mutableEdges,
      dispatch,
      darkMode,
      theme.palette.primary.main,
      workflowId,
    ],
  );

  const onDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      onUserEdit?.();

      const typeData = JSON.parse(
        event.dataTransfer.getData('application/reactflow'),
      );
      if (!typeData?.type || !reactFlowInstance) return;

      const baseX = window.innerWidth / 2.5;
      const baseY = window.innerHeight / 2;

      // Increase offset on every drop
      const offset = dropCountRef.current * 10;
      dropCountRef.current += 1;

      const offsetPosition = reactFlowInstance.screenToFlowPosition({
        x: baseX + offset,
        y: baseY + offset,
      });

      const newNode = {
        id: `function_${+new Date()}`,
        type: typeData.type,
        position: offsetPosition,
        draggable: true,
        data: {
          label: typeData.functionType,
          functionType: typeData.functionType,
          icon: typeData.icon,
          config: {},
        },
      };

      dispatch(
        setNodesForWorkflow({ workflowId, nodes: [...mutableNodes, newNode] }),
      );
    },
    [mutableNodes, dispatch, reactFlowInstance, workflowId],
  );

  const onNodeDragStop = useCallback(
    (event, node) => {
      onUserEdit?.();

      dispatch(
        setNodesForWorkflow({
          workflowId,
          nodes: mutableNodes.map((n) =>
            n.id === node.id ? { ...n, position: node.position } : n,
          ),
        }),
      );
    },
    [mutableNodes, dispatch, workflowId],
  );

  const onNodesChange = useCallback(
    (changes) => {
      onUserEdit?.();

      const updated = applyNodeChanges(changes, mutableNodes);
      dispatch(setNodesForWorkflow({ workflowId, nodes: updated }));
    },
    [mutableNodes, dispatch, workflowId],
  );

  const onEdgesChange = useCallback(
    (changes) => {
      onUserEdit?.();

      const updated = applyEdgeChanges(changes, mutableEdges);
      dispatch(setEdgesForWorkflow({ workflowId, edges: updated }));
    },
    [mutableEdges, dispatch, workflowId],
  );

  const onEdgeClick = useCallback(
    // (event, edge) => {
    //   event.stopPropagation();
    //   dispatch(
    //     setEdgesForWorkflow({
    //       workflowName,
    //       edges: mutableEdges.filter((e) => e.id !== edge.id),
    //     })
    //   );
    // },
    // [mutableEdges, dispatch, workflowName]
    (event, edge) => {
      event.stopPropagation();
      onUserEdit?.();

      dispatch(removeEdgeAndUnmapFields({ workflowId, edgeId: edge.id }));
    },
    [dispatch, workflowId],
  );

  const handleAddEntityClick = () => {
    // if (!isSaved) {
    //   toast.error('Please save the workflow before adding entities.',{autoClose: 4000 });
    //   return;
    // }
    setOpenEntityDialog(true);
  };

  // const handleProceedSecond = () => {
  //   setOpenProceedNext(false);
  //   navigate(`/parentworkflow/${workflowName}`, {
  //     state: { proceedSecondMapping: true, secondaryDestinationId },
  //     replace: true,
  //   });
  // };

  // const handleProceedSecond = () => {
  //   setOpenProceedNext(false);
  //   const nextIndex = (decisionMeta?.currentIndex ?? 0) + 1;
  //   const nextDestId = decisionMeta?.destIds?.[nextIndex] || null;

  //   console.log('proceed→', nextIndex, nextDestId, decisionMeta);

  //   navigate(`/parentworkflow/${workflowName}`, {
  //     replace: true,
  //     state: {
  //       proceedNextMapping: true,
  //       proceedSecondMapping: true,
  //       secondaryDestinationId: nextDestId,
  //       decisionMeta: { ...decisionMeta, currentIndex: nextIndex },
  //     },
  //   });
  // };

  // const handleProceedSecond = () => {
  //   setOpenProceedNext(false);
  //   const nextIndex = (decisionMeta?.currentIndex ?? 0) + 1;
  //   const nextDestId =
  //     decisionMeta?.destIds?.[nextIndex] ?? secondaryDestinationId ?? null;

  //   const nextState = {
  //     proceedNextMapping: true,
  //     proceedSecondMapping: true,
  //     secondaryDestinationId: nextDestId,
  //   };
  //   if (decisionMeta)
  //     nextState.decisionMeta = { ...decisionMeta, currentIndex: nextIndex };

  //   navigate(`/parentworkflow/${workflowName}`, {
  //     replace: true,
  //     state: nextState,
  //   });
  // };

  const handleProceedSecond = () => {
    setOpenProceedNext(false);

    // carry meta forward and bump the index
    let nextMeta = decisionMeta || null;
    let nextDestName = '';
    if (nextMeta?.destIds?.length) {
      const nextIndex = Math.min(
        nextMeta.destIds.length - 1,
        (nextMeta.currentIndex ?? 0) + 1,
      );
      nextMeta = { ...nextMeta, currentIndex: nextIndex };
      nextDestName =
        nextMeta.destNames?.[nextIndex] ||
        mutableNodes.find((n) => n.id === nextMeta.destIds[nextIndex])?.data
          ?.label ||
        `Mapping ${nextIndex + 1}`;
    }

    navigate(`/parentworkflow/${workflowId}`, {
      state: {
        proceedNextMapping: true,
        decisionMeta: nextMeta,
        secondaryDestinationId: location.state?.secondaryDestinationId ?? null,
        dest2Name: nextDestName,
        sourceName: mutableNodes.find((n) => n.type === 'sourceNode')?.data
          ?.label,
        dest1Name: location.state?.dest1Name,
      },
      replace: true,
    });
  };

  // useEffect(() => {
  //   const handleKeyDown = (event) => {
  //     if (event.key === 'Delete') {
  //       if (selectedNodeIds.length > 0) {
  //         const functionNodeIdsToDelete = mutableNodes
  //           .filter(
  //             (n) => selectedNodeIds.includes(n.id) && n.type === 'functionNode'
  //           )
  //           .map((n) => n.id);

  //         let updatedNodes = mutableNodes.filter(
  //           (n) => !functionNodeIdsToDelete.includes(n.id)
  //         );

  //         let updatedEdges = mutableEdges.filter(
  //           (e) =>
  //             !functionNodeIdsToDelete.includes(e.source) &&
  //             !functionNodeIdsToDelete.includes(e.target)
  //         );

  //         const fieldEdgeIdsToDelete = selectedEdgeIds.filter((edgeId) => {
  //           const edgeObj = mutableEdges.find((e) => e.id === edgeId);
  //           return (
  //             edgeObj &&
  //             edgeObj.sourceHandle &&
  //             edgeObj.targetHandle &&
  //             edgeObj.sourceHandle.includes('__') &&
  //             edgeObj.targetHandle.includes('__')
  //           );
  //         });

  //         if (fieldEdgeIdsToDelete.length > 0) {
  //           updatedEdges = updatedEdges.filter(
  //             (e) => !fieldEdgeIdsToDelete.includes(e.id)
  //           );
  //         }

  //         if (
  //           functionNodeIdsToDelete.length > 0 ||
  //           fieldEdgeIdsToDelete.length > 0
  //         ) {
  //           dispatch(
  //             setNodesForWorkflow({ workflowName, nodes: updatedNodes })
  //           );
  //           dispatch(
  //             setEdgesForWorkflow({ workflowName, edges: updatedEdges })
  //           );
  //           setSelectedNodeIds([]);
  //           setSelectedEdgeIds([]);
  //           toast.success(
  //             `Deleted ${functionNodeIdsToDelete.length} function node(s) and ${fieldEdgeIdsToDelete.length} edge(s)`,
  //             { autoClose: 1800 }
  //           );
  //         }
  //       }
  //     }
  //   };

  //   window.addEventListener('keydown', handleKeyDown);
  //   return () => window.removeEventListener('keydown', handleKeyDown);
  // }, [selectedNodeIds, selectedEdgeIds, mutableNodes, mutableEdges, dispatch, workflowName]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Delete') {
        let updatedNodes = mutableNodes;
        let updatedEdges = mutableEdges;
        let deletedFnNodes = 0;
        let deletedEdges = 0;

        if (selectedNodeIds.length > 0) {
          const functionNodeIdsToDelete = mutableNodes
            .filter(
              (n) =>
                selectedNodeIds.includes(n.id) &&
                n.type === 'functionNode' &&
                n.selected,
            )
            .map((n) => n.id);

          if (functionNodeIdsToDelete.length > 0) {
            updatedNodes = mutableNodes.filter(
              (n) => !functionNodeIdsToDelete.includes(n.id),
            );
            updatedEdges = mutableEdges.filter(
              (e) =>
                !functionNodeIdsToDelete.includes(e.source) &&
                !functionNodeIdsToDelete.includes(e.target),
            );
            deletedFnNodes = functionNodeIdsToDelete.length;
          }
        }

        console.log('--- Delete Key Pressed ---');
        console.log('Selected node IDs:', selectedNodeIds);
        // console.log('Function nodes to delete:', functionNodeIdsToDelete);
        console.log('Selected edge IDs:', selectedEdgeIds);

        // if (selectedEdgeIds.length > 0) {
        //   updatedEdges = updatedEdges.filter(
        //     (e) => !selectedEdgeIds.includes(e.id)
        //   );
        //   deletedEdges = selectedEdgeIds.length;
        // }

        if (deletedFnNodes > 0 || deletedEdges > 0) {
          dispatch(setNodesForWorkflow({ workflowId, nodes: updatedNodes }));
          dispatch(setEdgesForWorkflow({ workflowId, edges: updatedEdges }));
          setSelectedNodeIds([]);
          setSelectedEdgeIds([]);
          toast.success(
            `Deleted ${
              deletedFnNodes > 0 ? `${deletedFnNodes} function nodes ` : ''
            }${deletedEdges > 0 ? `${deletedEdges} edges` : ''}`,
            { autoClose: 1800 },
          );
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    selectedNodeIds,
    selectedEdgeIds,
    mutableNodes,
    mutableEdges,
    dispatch,
    workflowId,
  ]);

  //   useEffect(() => {
  //   const autoSaveBeforeUnload = (e) => {
  //     handleSaveClose();

  //     e.preventDefault();
  //     e.returnValue = '';
  //   };

  //   window.addEventListener('beforeunload', autoSaveBeforeUnload);
  //   return () => window.removeEventListener('beforeunload', autoSaveBeforeUnload);
  // }, []);

  return (
    <>
      <Box
        display="flex"
        height="100vh"
        sx={{
          backgroundColor: '#F9F9FB',
        }}
      >
        <Box flex={1}>
          <Box
            sx={{
              position: 'sticky',
              top: 35,
              zIndex: 100,
              bgcolor: 'background.default',
              px: 2,
              py: 1,
              borderBottom: '1px solid',
              borderColor: darkMode ? 'rgba(255, 255, 255, 0.12)' : '#e0e0e0',
            }}
          >
            <FunctionToolbar
              rightSideComponents={
                <>
                  <EdgeInfoBox />
                  <Button
                    variant="contained"
                    size="small"
                    sx={containedButtonStyle}
                    color="primary"
                    onClick={handleAddEntityClick}
                  >
                    + Add Entities
                  </Button>
                  <Button
                    variant="outlined"
                    sx={getOutlinedGradientButtonStyle()}
                    size="small"
                    onClick={() => setOpenConfirm(true)}
                  >
                    Discard Mapping
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={containedButtonStyle}
                    size="small"
                    onClick={() => setOpenSaveCloseDialog(true)}
                  >
                    Save & Close
                  </Button>
                </>
              }
            />
          </Box>

          {mappingLoading ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100vh',
              }}
            >
              <Typography>Loading fields...</Typography>
              <CircularProgress sx={{ fontSize: '2rem', color: '#2196F3' }} />
            </Box>
          ) : (
            <Box style={{ height: '100%', width: '100%' }}>
              <ReactFlow
                className="custom-flow"
                nodes={mutableNodes}
                edges={mutableEdges}
                onConnect={onConnect}
                edgeTypes={edgeTypes}
                nodeTypes={nodeTypes}
                fitView
                defaultViewport={{ zoom: 0.5, x: 0, y: 0 }}
                defaultEdgeOptions={{
                  type: 'custom',
                  markerEnd: {
                    type: MarkerType.ArrowClosed,
                    color: darkMode ? theme.palette.text.primary : '#000',
                  },
                }}
                onDrop={onDrop}
                onDragOver={onDragOver}
                onInit={setReactFlowInstance}
                nodesDraggable={false}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onNodeDragStop={onNodeDragStop}
                onEdgeClick={onEdgeClick}
                zoomOnDoubleClick={false}
                maxZoom={0.8}
                minZoom={0.7}
                panOnScroll
                onSelectionChange={() => {
                  const nodeIds = mutableNodes.map((n) => n.id);

                  const edgeIds = mutableEdges
                    .filter(
                      (e) =>
                        nodeIds.includes(e.source) &&
                        nodeIds.includes(e.target),
                    )
                    .map((e) => e.id);

                  console.log('Selection changed:');
                  console.log('Selected nodeIds:', nodeIds);
                  console.log('Selected edgeIds (computed):', edgeIds);

                  setSelectedNodeIds((prev) =>
                    prev.length !== nodeIds.length ||
                    prev.some((id, idx) => id !== nodeIds[idx])
                      ? nodeIds
                      : prev,
                  );
                  setSelectedEdgeIds((prev) =>
                    prev.length !== edgeIds.length ||
                    prev.some((id, idx) => id !== edgeIds[idx])
                      ? edgeIds
                      : prev,
                  );
                }}
              >
                <Background
                  color={
                    darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'
                  }
                  gap={16}
                  size={1}
                />

                <Controls
                  position="bottom-left"
                  showInteractive={false}
                  showZoom={false}
                  showFitView={false}
                  style={{ bottom: 250, right: 'auto', left: -6, top: 'auto' }}
                >
                  <ControlButton onClick={() => reactFlowInstance.zoomIn?.()}>
                    <Add />
                  </ControlButton>
                  <ControlButton onClick={() => reactFlowInstance.zoomOut?.()}>
                    <Remove />
                  </ControlButton>
                  <ControlButton
                    onClick={() =>
                      reactFlowInstance.fitView?.({ duration: 500 })
                    }
                  >
                    <CenterFocusStrong />
                  </ControlButton>
                </Controls>
              </ReactFlow>
            </Box>
          )}

          <EntitySelectionDialog
            requireEntityChangeForNext={true}
            open={openEntityDialog}
            onClose={() => setOpenEntityDialog(false)}
            selectedNode={null}
            updatedConfig={updatedConfig}
            restrictToActiveDest={Array.isArray(decisionMeta?.destIds)}
            activeDestinationId={activeDestId}
            lockSource={Array.isArray(decisionMeta?.destIds)}
            decisionMeta={decisionMeta}
            secondaryDestinationId={secondaryDestinationId}
          />
        </Box>
      </Box>

      <ConfirmDialog
        open={openConfirm}
        title="Discard All Mappings?"
        content="Are you sure you want to discard all mappings? This will remove all connections and function nodes."
        onCancel={() => setOpenConfirm(false)}
        onConfirm={handleConfirmDiscard}
      />

      <ConfirmDialog
        open={openValidation}
        title="No Mappings Detected"
        content="Please connect at least one source and destination field before saving."
        onCancel={() => setOpenValidation(false)}
        onConfirm={() => setOpenValidation(false)}
        hideConfirm={true}
      />

      <SaveCloseDialog
        open={openSaveCloseDialog}
        onClose={() => setOpenSaveCloseDialog(false)}
        onConfirm={(filterPayload) => {
          try {
            dispatch(
              setFilterExpressionForWorkflow({
                workflowId,
                filterExpression: filterPayload,
              }),
            );
          } catch (e) {
            console.error('Failed to set filter expression:', e);
          }
          setOpenSaveCloseDialog(false);
          handleSaveClose();
        }}
        onSkip={() => {
          try {
            dispatch(
              setFilterExpressionForWorkflow({
                workflowId,
                filterExpression: null,
              }),
            );
          } catch (e) {
            console.error('Failed to clear filter expression:', e);
          }
          setOpenSaveCloseDialog(false);
          handleSaveClose();
        }}
      />
      {/* <ProceedMappingDialog
        open={openProceedNext}
        onClose={() => {
          setOpenProceedNext(false);
          navigate(`/parentworkflow/${workflowName}`);
        }}
        onProceed={handleProceedSecond}
        mode="next"
        title="Mapping 1 Done → Proceeding to Mapping 2"
        source={sourceName}
        destination1={dest1Name}
        destination2={dest2Name}
      /> */}

      <ProceedMappingDialog
        open={openProceedNext}
        onClose={() => {
          setOpenProceedNext(false);
          navigate(`/parentworkflow/${workflowId}`);
        }}
        onProceed={() => handleProceedSecond()}
        mappings={(decisionMeta?.destIds || []).map((_, i) => ({
          sourceLabel: sourceName || 'Source',
          destinationLabel: decisionMeta?.destNames?.[i] || `Mapping ${i + 1}`,
        }))}
        activeIndex={(decisionMeta?.currentIndex ?? 0) + 1}
      />
    </>
  );
});

ETLMapping.propTypes = {
  updatedConfig: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  onUserEdit: PropTypes.func,
  onUserSave: PropTypes.func,
};

export default ETLMapping;
